import { create } from "zustand";
import { trackViewItem } from "@/lib/analytics";
// TYPE-ONLY, and it must stay that way: `lib/products.ts` is a server module
// (drizzle, D1, R2). A statement-form `import type` is erased before bundling,
// so nothing crosses the client boundary; an accidental value import here would
// pull the whole query layer into every product page's client bundle.
import type { PublicProductWithUrls } from "@/lib/products";

/**
 * The client-side product shape — what the grid, cards, carousel and detail
 * overlay consume.
 *
 * DERIVED from `PublicProductWithUrls`, never re-typed by hand. That type is
 * `Omit<Product, "reference" | "costPence"> & { imageUrl … }` — the allow-list
 * boundary with a documented leak history: `costPence` is COST OF GOODS
 * (migration 0066) and shipped in the public API from the day the column was
 * added. A hand-written copy of this shape would not exclude the NEXT
 * admin-only column automatically; a `Pick` of the public type does, and adding
 * `"costPence"` to the list below is now a COMPILE ERROR rather than a leak.
 *
 * ⚠ The `Partial` half is load-bearing. On `PublicProductWithUrls` these six
 * fields are required (`deliveryTiming` and `isAddon` are `NOT NULL` columns,
 * `slug`/`galleryImageKeys` are nullable-but-present). The client shape has
 * always treated them as optional — ~23 consumers build fixtures without them —
 * so a naked `Pick` would be a different type, not a derived one. This
 * intersection is structurally IDENTICAL to the interface it replaced.
 */
export type Product = Pick<
  PublicProductWithUrls,
  | "id"
  | "title"
  | "description"
  | "category"
  | "pricePence"
  | "imageKey"
  | "imageUrl"
  | "inStock"
  | "occasions"
> &
  Partial<
    Pick<
      PublicProductWithUrls,
      // `slug` — descriptive URL slug from the API; link builders fall back to
      //   `id` when absent (pre-slug rows).
      // `isAddon` — 1 = an add-on (card, ribbon, keepsake extra), shown with an
      //   "Add-on" badge and not orderable on its own (enforced at checkout).
      | "slug"
      | "imageUrlRaw"
      | "galleryImageKeys"
      | "galleryImageRawUrls"
      | "deliveryTiming"
      | "isAddon"
    >
  > & {
    /** Lightweight variants for list/card pricing ("From £X") + quick-add
     *  gating. Present only for products that have variants. NOT a `products`
     *  column — the API joins these in from `product_variants`, so this one
     *  genuinely cannot be derived from the public product shape. */
    variants?: { id: string; label: string; pricePence: number | null }[];
  };

interface ProductOverlayState {
  /** The product currently shown in the detail overlay */
  selectedProduct: Product | null;
  /** Whether the overlay (sheet/dialog) is open */
  overlayOpen: boolean;

  /** Open the product detail overlay — updates URL without triggering Next.js router */
  openProduct: (product: Product) => void;
  /** Close the product detail overlay — updates URL without triggering Next.js router */
  closeOverlay: () => void;
  /**
   * Clear the overlay WITHOUT touching `history`. A zustand store is a module
   * singleton, so it outlives the page that uses it: a customer who left
   * /products with the drawer open came back to it re-opening itself on a
   * product they never tapped this visit. `ProductsClient` calls this on
   * unmount so the next mount starts clean.
   *
   * Deliberately NOT `closeOverlay`: that one rewrites the URL, and on unmount
   * the URL already belongs to the page being navigated TO — stripping
   * `?product=` there would corrupt an unrelated route's address bar.
   */
  resetOverlay: () => void;
}

export const useProductOverlay = create<ProductOverlayState>((set) => ({
  selectedProduct: null,
  overlayOpen: false,

  openProduct: (product) => {
    set({ selectedProduct: product, overlayOpen: true });
    trackViewItem({
      productId: product.id,
      title: product.title,
      category: product.category,
      pricePence: product.pricePence,
    });
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      // Prefer the descriptive slug in the quick-view deep-link; fall back to
      // the stable id for any product without a slug yet.
      url.searchParams.set("product", product.slug || product.id);
      window.history.replaceState(null, "", url.pathname + url.search);
    }
  },

  closeOverlay: () => {
    set({ overlayOpen: false, selectedProduct: null });
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("product");
      window.history.replaceState(null, "", url.pathname + (url.search || ""));
    }
  },

  resetOverlay: () => set({ overlayOpen: false, selectedProduct: null }),
}));
